// What the card rail is allowed to CALL ITSELF in client-facing copy.
//
// ===========================================================================
// WHY THIS FILE EXISTS
// ===========================================================================
// There are TWO different Apple Pay questions, and this file used to conflate
// them into one blanket "never mention Apple Pay" rule. That rule was wrong.
//
//  (1) Do WE render an Apple Pay method — a button/tile the client taps in our
//      UI that hands off to a native sheet? NO. That needs Network
//      International's Hosted Sessions product, which is not enabled on this
//      outlet. We must not draw an Apple Pay button, list it as one of our
//      methods, or otherwise imply we integrated it.
//
//  (2) Does the N-GENIUS HOSTED PAYMENT PAGE we redirect to offer Apple Pay on
//      a capable device? YES (2026-07-19). The hosted paypage renders
//      the wallet options the device supports alongside the card form. So it is
//      TRUE, and useful, to reassure the client at the pay button that the
//      secure checkout takes a credit card or Apple Pay.
//
// So: no rendered Apple Pay METHOD (1), but the truthful reassurance SENTENCE
// about the hosted page is allowed and is exported below as
// SECURE_CHECKOUT_NOTE. It is deliberately worded as a property of the checkout
// page, not a promise that Apple Pay is available on every device — a client on
// a Windows desktop simply sees the card form, which is what the sentence's
// "credit card or" half already covers.
//
// The METHOD-LABEL helpers stay capability-derived: if a surface ever wants to
// NAME the rail "Card / Apple Pay" it must be because the server said so. GET
// /api/payments/mode is that channel (it already carries `mode` and
// `mit_enabled`). Add `apple_pay` there when NI enables Hosted Sessions, pass
// the mode payload into the helpers below, and every method label flips
// together. Default is card-only on purpose: a surface that forgets to pass the
// flag under-promises, which is the safe direction.
// ===========================================================================

// The reassurance line that sits under a primary Pay button. Not a method, not
// a button — a factual statement about what the secure page accepts. Safe to
// render unconditionally: it does not depend on the Hosted Sessions flag,
// because it describes the hosted paypage rather than anything we integrated.
export const SECURE_CHECKOUT_NOTE = 'Secure checkout with credit card or Apple Pay';

// SECURE_CHECKOUT_NOTE_REQUEST / _CHANGE_REQUEST (the request-submitting
// variants of the sentence above) are DELETED (2026-07-19). The reassurance may
// exist in exactly ONE place per surface — PayNowPanel, when a card leg exists
// (plan.needsCard), which renders SECURE_CHECKOUT_NOTE. The booking modals do
// not host PayNowPanel and render no secure line at all. Do not reintroduce
// per-surface variants.

// True only when the server has explicitly told us Apple Pay is live on the
// gateway account. Anything else — missing field, missing payload, a 404 from
// an older backend — is false.
export const applePayAvailable = (paymentsMode) => paymentsMode?.apple_pay === true;

// Name of the rail, for a method button / picker option.
export const cardRailLabel = (paymentsMode) =>
    (applePayAvailable(paymentsMode) ? 'Card / Apple Pay' : 'Pay by card');

// Sub-label under the rail: what happens when you choose it.
export const cardRailSubLabel = (paymentsMode) =>
    (applePayAvailable(paymentsMode)
        ? 'Enter card details on the secure payment page — Apple Pay too.'
        : 'Enter your card details on the secure payment page.');

// Short form for checkout panels that already sit under a "how do you want to
// pay" heading and only need the outcome, not the explanation.
export const cardRailShortSubLabel = (paymentsMode) =>
    (applePayAvailable(paymentsMode)
        ? 'Pay on the secure page — Apple Pay too.'
        : 'Pay on the secure payment page.');
